import { useApolloClient } from '@apollo/client/react';
import { InfoField } from '@components-clean/core/info-field';
import PageBreadcrumbs from '@components-clean/core/page-breadcrumbs';
import { IsPopOut } from '@components-clean/core/page-toolbar/page-toolbar';
import Navbar from '@components-clean/navbar';
import PageNav from '@components-clean/page-nav';
import CssBaseline from '@mui/material/CssBaseline';
import { classNames } from '@progress/kendo-react-common';
import LocalStorage from '@utils-clean/local-storage-service';
import throttle from 'lodash/throttle';
import React, { PropsWithChildren, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { withStyles } from 'tss-react/mui';
import { appStyles } from './app.data';
import Tabs from './tabs';
import ErrorBoundary from 'hoc/error-boundary';
import { useBoolean, useLayout } from 'hooks';
import './app.scss';
import { LocalContextPersistedKey } from '@contexts/local-state-context';
import { UserContext } from '@contexts/user-context';
import doLogout from '@utils/auth.service';
import { LayoutProviderProps } from '@utils/layouts';

interface AppLayoutProps extends PropsWithChildren<Omit<LayoutProviderProps, 'layout'>> {
    classes: Record<string, string>;
}

const AppLayout = ({ classes, children, currentTheme }: AppLayoutProps) => {
    const { t } = useTranslation('common');
    const {
        state: { tabs, pageBreadcrumbs },
    } = useLayout();
    const tabletMode = useBoolean(false);
    const isCollapsedSidebar = useBoolean(false);
    const castTabletOnDebounced = throttle(tabletMode.setTrue, 200);
    const castTabletOffDebounced = throttle(tabletMode.setFalse, 200);

    const handleWindowResize = () => {
        if (window.innerWidth <= 960) {
            if (!tabletMode.value) castTabletOnDebounced();
        } else if (tabletMode.value) castTabletOffDebounced();
    };

    useEffect(() => {
        window.addEventListener('resize', handleWindowResize);
        handleWindowResize();

        return () => window.removeEventListener('resize', handleWindowResize);
    });

    const userContext = useContext(UserContext);

    useEffect(() => {
        if (!userContext.user) return;
        isCollapsedSidebar.setValue(LocalStorage.getItem(userContext.user, 'PageNavIsCollapsed') === 'true');
    }, [userContext.user]);

    const [hasOrgChanged, setHasOrgChanged] = useState<boolean>(false);
    const client = useApolloClient();

    useEffect(() => {
        const handleStorageChange = (event: StorageEvent) => {
            if (event.key === `${userContext.user?.id}_ParentPageOrgKey`) {
                const lockedToOrgKey = sessionStorage.getItem(LocalContextPersistedKey.LockedToOrgKey);
                const currentOrgKey = userContext.user?.organisationKey;

                const parentPageOrgDoesntMatchCurrent = event.newValue !== currentOrgKey;
                const lockedToOrgKeyDoesntMatchCurrent = lockedToOrgKey !== currentOrgKey;

                setHasOrgChanged(parentPageOrgDoesntMatchCurrent || lockedToOrgKeyDoesntMatchCurrent);
            }
            if (event.key === `${userContext.user?.id}_IsLoggingOut`) {
                doLogout(client, userContext, 'IsLoggingOut');
            }
        };

        window.addEventListener('storage', handleStorageChange);

        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    // If and when the user changes organisation (primarily after a reload), check if the session is locked to an org,
    // and if different to the newly selected org, lock out the screen.
    useEffect(() => {
        const lockedToOrgKey = sessionStorage.getItem(LocalContextPersistedKey.LockedToOrgKey);

        if (!lockedToOrgKey) return;

        if (lockedToOrgKey === userContext.user?.organisationKey) return;

        setHasOrgChanged(true);
    }, [userContext.user?.organisationKey]);

    return (
        <div className={classNames(classes.root, { 'org-has-changed': hasOrgChanged })}>
            {hasOrgChanged && (
                <div className="opra-default-theme org-has-changed-overlay">
                    <InfoField message={t('organisationChangeLockOutMessage')} type="Error" />
                </div>
            )}
            <CssBaseline />
            {!IsPopOut() && <PageNav tabletMode={tabletMode.value} isCollapsed={isCollapsedSidebar} />}
            <ErrorBoundary history={history}>
                <main className={classNames(classes.content, 'fa-width-auto')}>
                    <Navbar
                        tabletMode={isCollapsedSidebar.value || tabletMode.value}
                        isLegacy={currentTheme === 'legacy'}
                    />
                    {!!pageBreadcrumbs?.length && <PageBreadcrumbs breadcrumbs={pageBreadcrumbs} />}
                    <div style={{ paddingTop: 0 }}>
                        <Tabs tabs={tabs} />
                    </div>
                    {children}
                </main>
            </ErrorBoundary>
        </div>
    );
};

export default withStyles(AppLayout, appStyles);
